/* eslint-disable react/require-default-props */
import { Check, CloudDownload } from '@styled-icons/boxicons-regular';
import {
  CURRENCY_LIMIT,
  CURRENCY_SYMBOLS,
  Currency,
  PRODUCT_TYPE,
  ProductCollectionItem,
  errToString,
} from '@typedream/data';
import { Button } from '@typedream/ui';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, memo, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useSpaceIDFromPath } from '../../data/space/hooks';
import OptionsContainer from '../../sharedComponents/Options';
import OptionsRadio from '../../sharedComponents/Options/OptionsRadio';
import Popup from '../../sharedComponents/TextEditor/components/Popup';
import {
  useCheckPaypalConnectionQuery,
  useCheckStripeConnectionQuery,
  useCreateProductMutation,
  useGetSpaceQuery,
} from '../../store';
import sharedStyles from '../../styles/Shared.module.css';
import { IPrice } from '../../types';
import { PriceInput } from '../PriceInput';
import styles from './Products.module.css';

export const PopupCreateProduct = memo(
  ({
    open,
    setopen,
    onCreateCallback,
  }: {
    open: boolean;
    setopen: Dispatch<SetStateAction<boolean>>;
    onCreateCallback?: (prod: ProductCollectionItem) => void;
  }) => {
    const spaceID = useSpaceIDFromPath();
    const router = useRouter();
    const [isLoading, setisLoading] = useState(false);
    const [name, setname] = useState('');
    const [nameErr, setnameErr] = useState(false);
    const [price, setprice] = useState<IPrice | undefined>(undefined);
    const [priceOffer, setpriceOffer] = useState<'free' | 'paid'>('free');
    const [priceErr, setpriceErr] = useState(undefined);
    const { mutate: createProduct } = useCreateProductMutation({
      fixedCacheKey: `createProduct-${spaceID}`,
    });
    const { data: space } = useGetSpaceQuery([spaceID]);
    const { data: stripeConnection } = useCheckStripeConnectionQuery([spaceID]);
    const { data: paypalConnection } = useCheckPaypalConnectionQuery([spaceID]);

    const isPaymentConfigured =
      stripeConnection?.status === 'configured' || paypalConnection?.status === 'configured';

    const currency = useMemo(() => price?.currency || Currency.USD, [price]);

    const resetInput = () => {
      setname('');
      setnameErr(false);
      setprice(undefined);
    };
    const onCreateProduct = async () => {
      if (!name) setnameErr(true);
      if (!name) {
        return;
      }
      if (
        priceOffer === 'paid' &&
        (!price ||
          Number(price.amount) < Math.floor(CURRENCY_LIMIT[currency] / 1000) ||
          Number(price.amount) > CURRENCY_LIMIT[currency])
      ) {
        setpriceErr(
          'Please enter a price between' +
            `${CURRENCY_SYMBOLS[currency]}${Math.floor(
              CURRENCY_LIMIT[currency] / 1000
            ).toLocaleString()}` +
            ' and ' +
            `${CURRENCY_SYMBOLS[currency]}${CURRENCY_LIMIT[currency].toLocaleString()}`
        );
        return;
      }
      setisLoading(true);
      try {
        let productPrice = Number(price?.amount || 0);

        if (price?.specialCase === 'zero') {
          productPrice /= 100;
        } else if (price?.specialCase === 'three') {
          productPrice = Math.round(productPrice / 10) / 10;
        }

        const prod = await createProduct({
          productData: {
            product_name: name,
            product_price: productPrice.toString(),
            product_currency: price?.currency,
            product_type: PRODUCT_TYPE.DIGITAL_DOWNLOAD,
            product_images: [
              'https://typedream-assets.sfo3.cdn.digitaloceanspaces.com/product_image.png',
            ],
          },
          spaceID,
        }).unwrap();
        if (onCreateCallback) {
          onCreateCallback(prod);
        } else {
          toast.success(`Created product ${name}`);
          router.push(`/settings/${space?.slug}/${spaceID}/products/${prod.id}`);
        }
        setopen(false);
      } catch (e) {
        toast.error(errToString(e));
      }
      resetInput();
      setisLoading(false);
    };
    return (
      <Popup
        width={700}
        height={900}
        open={open}
        setopen={setopen}
        className="p-6 is-flex is-flex-direction-column is-justify-content-space-between"
      >
        <div>
          <div className="is-size-5 has-text-weight-medium with-text-color mb-5">New Product</div>
          <div className={styles.blockItem}>
            <div className="is-size-6 with-text-color mb-1">Product Name</div>
            <input
              className={classNames(sharedStyles.inputMedium, 'has-fullwidth', {
                [sharedStyles.inputError]: nameErr,
              })}
              type="text"
              value={name}
              placeholder="e.g. Zero to Launch Playbook"
              onChange={(e) => {
                setnameErr(false);
                setname(e.target.value);
              }}
            />
          </div>
          <div className={styles.blockItem}>
            <div className="is-size-6 with-text-color mb-1">Product Type</div>
            <OptionsContainer selected={false} className="is-justify-content-space-between">
              <div className={styles.productIconBackground}>
                <CloudDownload size={25} className="with-accent-text-color" />
              </div>
              <div className="is-flex-shrink-1 is-flex-grow-1 px-3">
                <div className="is-size-6 with-text-color has-text-weight-medium">
                  Digital Download
                </div>
                <div className="is-subtitle">Offer a file or collection of files to download</div>
              </div>
              <OptionsRadio selected />
            </OptionsContainer>
          </div>
          <div className={styles.blockItem}>
            <div className="is-size-6 with-text-color mb-1">Product Offer</div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <OptionsContainer
                selected={priceOffer === 'free'}
                className="is-justify-content-space-between"
                style={{ gap: 12 }}
                onClick={() => setpriceOffer('free')}
              >
                <div style={{ flex: 1 }}>
                  <div className="is-size-6 with-text-color has-text-weight-medium">Free</div>
                  <div className="is-subtitle">
                    Give your product away for free to collect email address
                  </div>
                </div>
                <OptionsRadio selected={priceOffer === 'free'} />
              </OptionsContainer>
              <div>
                <OptionsContainer
                  selected={priceOffer === 'paid'}
                  className="is-justify-content-space-between"
                  style={{ gap: 12 }}
                  onClick={() => setpriceOffer('paid')}
                  disabled={!isPaymentConfigured}
                >
                  <div style={{ flex: 1 }}>
                    <div className="is-size-6 with-text-color has-text-weight-medium">Paid</div>
                    <div className="is-subtitle">Sell your product with a one-time payment</div>
                  </div>
                  <OptionsRadio selected={priceOffer === 'paid'} />
                </OptionsContainer>
                {!isPaymentConfigured && (
                  <div className="is-small-subtitle">
                    Please connect a Stripe/PayPal account to accept payments.
                    <Link
                      className="with-accent-text-color with-cursor-pointer"
                      href={`/settings/${space?.slug}/${space?.id}/payments`}
                    >
                      Connect Stripe/PayPal in Settings &rarr;
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
          {priceOffer === 'free' && (
            <div className={styles.blockItem}>
              <div className="is-subtitle">
                <Check size={20} className="with-accent-text-color mr-2" />
                Ask users for their email to view the download page
              </div>
              <div className="is-subtitle">
                <Check size={20} className="with-accent-text-color mr-2" />
                Send a download link to their email
              </div>
            </div>
          )}
          {priceOffer === 'paid' && isPaymentConfigured && (
            <div className={styles.blockItem}>
              <div className="is-size-6 with-text-color mb-1">Product Price</div>
              <PriceInput
                error={!!priceErr}
                onUpdate={(val) => {
                  setpriceErr(undefined);
                  setprice(val);
                }}
              />
              {priceErr ? (
                <div className="with-danger-text-color is-size-7">{priceErr}</div>
              ) : (
                <div className="is-subtitle is-size-7">
                  Price your product between {CURRENCY_SYMBOLS[currency]}
                  {Math.floor(CURRENCY_LIMIT[currency] / 1000).toLocaleString()} and{' '}
                  {CURRENCY_SYMBOLS[currency]}
                  {CURRENCY_LIMIT[currency].toLocaleString()}.
                </div>
              )}
              {currency === Currency.HUF ? (
                <div className="is-size-7">
                  Only decimal value can be accepted for {currency.toUpperCase()}
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-flex-end mt-5">
          <Button variant="outline" className="mr-2" onClick={resetInput}>
            Discard
          </Button>
          <Button
            onClick={onCreateProduct}
            isLoading={isLoading}
            disabled={(priceOffer === 'paid' && !(price && price.amount)) || !name}
          >
            Create Product
          </Button>
        </div>
      </Popup>
    );
  }
);
